import { inject, Injectable } from '@angular/core';

import { Client } from 'src/providers/client/client';

import { RentalUnitsService } from 'src/shared/services/rental-units.service';

import { PLATFORM_MARK_LOGOS, formatPlatformName, getConnectSteps, getConnectingLabel, getListingPlatform } from 'src/shared/constants/listing-platforms';
import { CONCIERGE_SETUP_STATUS, NEEDS_SETUP_STATUS } from 'src/pages/property-listings/property-listings.status';

import { DateTime } from 'luxon';
import { Subject } from 'rxjs';

export interface AddressListing {
  id: number;
  address_id: number;
  platform_id: string;
  platform: string;
  status: string;
  created_at: string;
  updated_at: string;
  // When TIDY last released an optimization report for this listing (null until
  // the first report is released). Drives the Listings page "Last optimized" line.
  last_optimized_at?: string | null;
  address?: any;

  // Client-only rows the Listings page synthesizes for an address × channel
  // with no backend listing (status `needs_setup` / `concierge_setup`). Their
  // ids are negative so they can never collide with a real address_listing.
  isPlaceholder?: boolean;

  // Mock room-listing rows only (by-room showcase units) — real listings never
  // set these.
  space_id?: number;
  space_name?: string;
  unit_id?: number;
  property_label?: string;
  property_color?: string;
  isMockRoomListing?: boolean;
}

// Seam to disable the mock room-listing fan-out without touching callers.
const USE_DUMMY_ROOM_LISTINGS = true;

// Stable per-unit color for the by-room showcase, kept out of the shared
// stateColor() palette since these rows aren't unit-state driven.
const ROOM_LISTING_UNIT_COLORS: Record<number, string> = {
  9: '#0277BD',
  10: '#6A1B9A',
};

// Status dot colors. Grey is the fallback so an unrecognized status still reads
// as "not live" rather than borrowing a meaning it hasn't earned.
const STATUS_GREY = '#8E8E93';
const STATUS_LISTED = 'var(--tidy-success, #41CAB7)';
const STATUS_CONNECTING = 'var(--tidy-warning, #FECD4E)';

export interface ChannelTableRow {
  id: number;
  addressId: number;
  listingData: AddressListing;
  // Status/platform without the markup their cell values carry, so sorting and
  // searching compare words rather than badge/dot spans.
  statusLabel: string;
  platformLabel: string;
  'Platform': {
    value: string;
    action: null;
  };
  'Property': {
    value: string;
    action: null;
    color: string;
    addressId: number;
  };
  'Listing ID': {
    value: string;
    action: null;
  };
  'Status': {
    value: string;
    action: null;
  };
  'last-column': {
    value: number;
    action: null;
    hasChevron: true;
  };
}

@Injectable({
  providedIn: 'root'
})
export class MarketingChannelsService {
  private readonly client = inject(Client);
  private readonly rentalUnitsService = inject(RentalUnitsService);

  // Fires when the listing detail page removes a listing on mobile, where that page
  // is a route rather than a panel — so the channels table's panelClosed() hook
  // emits at subscribe time instead of on return and can't be relied on.
  readonly listingsChanged$ = new Subject<void>();

  async fetchListings(addresses: any[]): Promise<AddressListing[]> {
    const promises = addresses.map(address =>
      this.client.getAddressListings(address.id)
        .then((listings: any[]) => (listings || []).map(l => ({ ...l, address })))
        .catch(() => [] as AddressListing[])
    );
    const results = await Promise.all(promises);
    const listings = results.flat();
    if (USE_DUMMY_ROOM_LISTINGS) {
      // Link the demo by-room units to real addresses first, so room rows land
      // under a real property filter even when this tab is the first surface
      // loaded in the session.
      this.rentalUnitsService.linkDemoUnitToFirstAddress(
        addresses.map(address => ({ viewValue: address?.address_name || address?.address1 || '', value: address?.id }))
      );
      listings.push(...(await this.buildMockRoomListings()));
    }
    return listings;
  }

  // Fans out each by-room unit's room-level SpaceListings into AddressListing
  // rows, so the shared channels table can render them alongside real listings
  // without any template changes.
  private async buildMockRoomListings(): Promise<AddressListing[]> {
    const units = await this.rentalUnitsService.getByRoomUnits();
    const rows: AddressListing[] = [];
    units.forEach(unit => {
      const rooms = (unit.spaces || []).filter(space => space.type === 'room');
      rooms.forEach((space, spaceIndex) => {
        (space.listings || []).forEach((listing, listingIndex) => {
          rows.push({
            id: 900000 + unit.id * 100 + spaceIndex * 10 + listingIndex,
            address_id: unit.address_id ?? -unit.id,
            platform_id: listing.platform_id || '',
            platform: listing.platform,
            status: listing.status === 'syndicating' ? 'connecting' : listing.status,
            created_at: '2026-06-01T12:00:00.000Z',
            updated_at: '2026-06-15T09:30:00.000Z',
            unit_id: unit.id,
            space_id: space.id,
            space_name: space.name,
            property_label: `${unit.property_name} · ${space.name}`,
            property_color: ROOM_LISTING_UNIT_COLORS[unit.id] || '#1E2125',
            isMockRoomListing: true,
          });
        });
      });
    });
    return rows;
  }

  buildRows(listings: AddressListing[], addresses: any[]): ChannelTableRow[] {
    return listings.map(listing => ({
      id: listing.id,
      addressId: listing.address_id,
      listingData: listing,
      statusLabel: this.formatStatus(listing.platform, listing.status),
      platformLabel: this.formatPlatform(listing.platform),
      'Platform': {
        value: this.platformCellHtml(listing.platform),
        action: null
      },
      'Property': {
        value: listing.property_label || listing.address?.address_name || listing.address?.address1 || `Property #${listing.address_id}`,
        action: null,
        color: listing.property_label ? (listing.property_color || '#1E2125') : (addresses?.find((a: any) => a.id === listing.address_id)?.color || '#1E2125'),
        addressId: listing.address_id
      },
      'Listing ID': {
        value: listing.platform_id || '-',
        action: null
      },
      'Status': {
        value: this.statusCellHtml(listing.platform, listing.status),
        action: null
      },
      'last-column': {
        value: listing.id,
        action: null,
        hasChevron: true as const
      }
    }));
  }

  filterByProperty(rows: ChannelTableRow[], propertyId: number | null): ChannelTableRow[] {
    if (!propertyId) {
      return rows;
    }
    return rows.filter(row => row.addressId === propertyId);
  }

  // Platform badge + name as cell HTML (the table renders cell values with
  // innerHTML) — the same round brand badge the listings modal draws, at table
  // size via the global .platform-avatar modifiers. Unbadged platforms fall
  // back to their full-colour logo; unknown ones to the bare name.
  platformCellHtml(platform: string): string {
    const mark = PLATFORM_MARK_LOGOS[platform];
    const logo = mark || getListingPlatform(platform)?.logo || '';
    const name = this.formatPlatform(platform);
    if (!logo) {return name;}
    const brandClass = mark ? ` ${platform}` : '';
    return `<span class="platform-avatar platform-avatar--sm platform-avatar--inline${brandClass}">` +
      `<img src="${logo}" alt=""></span>${name}`;
  }

  formatPlatform(platform: string): string {
    if (platform === 'direct') {return 'Direct';}
    return formatPlatformName(platform);
  }

  // The platform's handover steps as numbered sentences (the structured steps
  // drive the listing detail stepper; this flat form is for plain-text uses).
  getCohostInstructions(platform: string): string[] {
    return getConnectSteps(platform).map((step, index) => `${index + 1}. ${step.title}`);
  }

  // Normalizes whatever the customer pasted (bare id, listing URL, multicalendar URL…)
  // into the platform's canonical listing id, or null when it can't be read. Long-term /
  // mid-term syndication platforms only need a non-empty value.
  parseListingId(input: string, platform: string): string | null {
    const trimmed = (input || '').trim();
    if (!trimmed) {return null;}
    if (platform === 'airbnb') {
      return this.parseAirbnbListingId(trimmed);
    }
    if (platform === 'vrbo') {
      if (/^\d+$/.test(trimmed)) {return trimmed;}
      const vrboMatch = trimmed.match(/vrbo\.[a-z.]+\/(\d+)(?:[/?#]|$)/i);
      return vrboMatch ? vrboMatch[1] : null;
    }
    if (platform === 'booking_com') {
      const bookingMatch = trimmed.match(/booking\.com\/hotel\/[a-z]{2}\/([a-z0-9-]+)(?:\.[a-z]+|[/?#]|$)/i);
      if (bookingMatch) {return bookingMatch[1];}
      return /^[a-z0-9-]+$/i.test(trimmed) ? trimmed : null;
    }
    return trimmed;
  }

  invalidListingIdMessage(platform: string): string {
    if (platform === 'airbnb') {
      return 'Enter a numeric Airbnb listing ID or its public URL (e.g. https://www.airbnb.com/rooms/12345). Multicalendar URLs are supported, but host-dashboard slugs are not.';
    }
    if (platform === 'vrbo') {
      return 'Enter a numeric VRBO listing ID or its property URL (e.g. https://www.vrbo.com/12345).';
    }
    if (platform === 'booking_com') {
      return 'Enter your Booking.com property slug or full URL (e.g. https://www.booking.com/hotel/us/your-property.html).';
    }
    return 'Please enter a valid listing.';
  }

  parseAirbnbListingId(input: string): string | null {
    const trimmed = (input || '').trim();
    if (!trimmed) {return null;}
    const patterns = [
      /\/listings\/editor\/(\d+)/,
      /\/rooms\/(\d+)/,
      /\/multicalendar\/(\d+)/,
      /\/calendar\/ical\/(\d+)/,
    ];
    for (const pattern of patterns) {
      const match = trimmed.match(pattern);
      if (match) {return match[1];}
    }
    return /^\d+$/.test(trimmed) ? trimmed : null;
  }

  formatStatus(platform: string, status: string): string {
    if (platform === 'tidy') {
      // The direct-booking dropdowns offer only Listed/Paused, so every off
      // state — including legacy 'unlisted' from the retired toggle — reads as
      // Paused. Must match the Direct Booking sites page's wording exactly.
      return status === 'listed' ? 'Listed' : 'Paused';
    }
    if (!status) {return 'Unknown';}
    if (status === NEEDS_SETUP_STATUS) {return 'Needs setup';}
    if (status === CONCIERGE_SETUP_STATUS) {return 'TIDY is creating your listing';}
    // Booking.com / Vrbo handovers read as what they're actually waiting on.
    if (status === 'connecting') {return getConnectingLabel(platform);}
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  // Colored dot + label, mirroring the Direct Booking sites page's Status cell.
  // The table renders cell values as HTML; a cell `color` field would render
  // tidy-tag (a home glyph) instead, which is wrong for a status. A row that
  // still needs the client's answer gets an exclamation mark instead of a dot.
  statusCellHtml(platform: string, status: string): string {
    if (status === NEEDS_SETUP_STATUS) {
      return `<span class="material-icons listing-status-alert" style="font-size:16px;color:${this.statusColor(platform, status)};` +
        `margin-right:5px;vertical-align:middle;">error_outline</span>${this.formatStatus(platform, status)}`;
    }
    return `<span style="display:inline-block;width:8px;height:8px;border-radius:50%;` +
      `background-color:${this.statusColor(platform, status)};margin-right:6px;vertical-align:middle;"></span>` +
      `${this.formatStatus(platform, status)}`;
  }

  private statusColor(platform: string, status: string): string {
    // The tidy row uses the same Listed/off palette as the channels now that
    // the wording matches too.
    switch (status) {
      case 'listed':
        return STATUS_LISTED;
      // 'syndicating' never reaches here — fetchListings normalizes it to 'connecting'.
      // Every pending state — including a listing TIDY is still creating — is amber;
      // green is reserved for a listing that's actually live.
      case 'connecting':
      case NEEDS_SETUP_STATUS:
      case CONCIERGE_SETUP_STATUS:
        return STATUS_CONNECTING;
      default:
        return STATUS_GREY;
    }
  }
}
